/**
 * usePayoutCurrency — the currency the instructor's payout copy must be shown in.
 *
 * Why this exists: the welcome and agreement steps of the wallet wizard run
 * BEFORE Stripe onboarding, so `stripe_accounts` has no row yet and
 * `useStripeConnect().account?.currency` is null in exactly the two screens
 * that need the value. `AuthContext` exposes no profile either. So the currency
 * is derived from `profiles.country` by the same policy
 * `stripe-connect-onboard` uses to create the account — which is what makes the
 * displayed minimum match the account the instructor will actually get
 * (spec COPY-01 AC-06).
 *
 * Precedence: `stripe_accounts.currency` → `resolveCountry(profiles.country)`
 * → `DEFAULT_COUNTRY.currency`.
 *
 * Never throws and never returns an empty value. `resolveCountry` throws for a
 * country outside `COUNTRY_MAP` on purpose (a silent US fallback when creating
 * a Stripe account would be a real defect); here, on a display surface, that
 * throw is caught and falls back to the default so the screen renders a real
 * amount instead of blank or NaN (spec COPY-01 AC-07).
 *
 * Goes through `walletRepository`, never `supabase` directly (CLAUDE.md).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import { walletRepository } from '@/features/wallet/repositories/supabaseWalletRepository';
import { resolveCountry, DEFAULT_COUNTRY } from '@/lib/stripeConnectPolicy';

export interface PayoutCurrency {
  /** ISO 4217 code, upper-cased. Always a usable currency, never empty. */
  currency: string;
  /** True while either source is still being fetched. */
  isLoading: boolean;
}

export function usePayoutCurrency(): PayoutCurrency {
  const { user } = useAuth();
  const { account, isLoading: isAccountLoading } = useStripeConnect();

  const { data: country, isLoading: isCountryLoading } = useQuery({
    queryKey: ['instructor-country', user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<string | null> => {
      if (!user?.id) return null;
      return walletRepository.fetchInstructorCountry(user.id);
    },
    // The instructor onboarding wizard mounts each step as its own component,
    // and more than one of them asks for the payout minimum, so without a
    // staleTime every step transition refetched a country that had not changed.
    // Five minutes matches `useUserProfile`, the other reader of this field.
    // `PayoutCountryStep` invalidates this key when the country is saved, and
    // invalidation ignores staleTime, so a fresh answer still lands immediately.
    staleTime: 5 * 60 * 1000,
  });

  const currency = useMemo(() => {
    if (account?.currency) return account.currency.toUpperCase();

    try {
      return resolveCountry(country ?? null).currency;
    } catch {
      return DEFAULT_COUNTRY.currency;
    }
  }, [account?.currency, country]);

  return { currency, isLoading: isAccountLoading || isCountryLoading };
}
